import { ImageSquare, Pencil, Trash } from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import { Entity } from '../../../../../../types/apiTypes';
import { useUser } from '../../../../../../utils/UserContext';

interface AccountInformationProps {
  imageUrl: string;
  loadingImage: boolean;
  hover: boolean;
  uploadedFile: File | null;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleDeleteClick: () => void;
  handleReplaceClick: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setAccountEditPopupVisible: (visible: boolean) => void;
}

function AccountInformation({
  imageUrl,
  loadingImage,
  hover,
  uploadedFile,
  handleMouseEnter,
  handleMouseLeave,
  handleDeleteClick,
  handleReplaceClick,
  handleFileChange,
  setAccountEditPopupVisible,
}: AccountInformationProps) {
  const { userRecord } = useUser();
  return (
    <>
      {userRecord && userRecord?.userType === Entity.Vendor && (
        <div className="flex w-full flex-col gap-y-4 rounded-lg border-[1px] border-bordergray p-3 lg:w-[50%]">
          <div className="flex w-full flex-row justify-between align-middle">
            <h2 className="text-lg font-semibold">Account Information</h2>
            <Pencil
              className="text-accent hover:cursor-pointer"
              onClick={() => setAccountEditPopupVisible(true)}
              size={24}
            />
          </div>
          <div className="flex w-full min-w-full flex-col items-center gap-6">
            <p className="text-[14px] text-[#858897]">Business Logo</p>
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="relative"
            >
              {/* Truck Image */}
              {userRecord && imageUrl?.length > 0 && !loadingImage ? (
                <div className="h-[138px] w-[138px] rounded-[4px] border-[1px] border-[#EAECF0]">
                  <img
                    src={imageUrl}
                    alt="Truck"
                    className="h-full w-full rounded-lg object-cover"
                  />
                </div>
              ) : !loadingImage ? (
                <>
                  <div className="flex h-[138px] w-[138px] flex-col items-center justify-center rounded-[4px] border-[1px] border-[#EAECF0] align-middle">
                    <div className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] align-middle">
                      <ImageSquare
                        size={40}
                        className="flex items-center justify-center text-graysecondary"
                        weight="bold"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loadingImage}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />
              )}
              {hover && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black bg-opacity-50 text-white">
                  <p className="text-sm font-semibold">Replace Photo</p>
                  <div className="flex w-full items-center justify-center space-x-2">
                    <button
                      onClick={handleDeleteClick}
                      className="rounded-full bg-red-500 p-2"
                    >
                      <Trash size={24} className="text-white" />
                    </button>
                    <button
                      onClick={handleReplaceClick}
                      className="rounded-full bg-yellow-500 p-2"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              )}
            </div>
            {uploadedFile && (
              <h2 className="max-w-[138px] text-green-400">
                Added {uploadedFile.name}
              </h2>
            )}
            <input
              type="file"
              id="fileInput"
              accept="image/png, image/jpeg, image/heic, image/heif"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {/* Info Section */}
            <div className={`grid w-full grid-cols-1 gap-1`}>
              {userRecord?.contactInfos?.length >= 1 && (
                <>
                  <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      First Name
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[0]?.first_name}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Last Name
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[0]?.last_name}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Phone Number
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[0]?.phone_number}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Position
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[0]?.position}
                    </p>
                  </div>
                </>
              )}
              {userRecord?.contactInfos?.length === 2 && (
                <>
                  <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Management Contact First Name
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[1]?.first_name}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Management Contact Last Name
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[1]?.last_name}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 bg-primarybg px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Management Contact Phone Number
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[1]?.phone_number}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 px-2 py-[2px] align-middle">
                    <p className="w-[35%] min-w-[35%] text-[14px] text-[#858897]">
                      Management Contact Position
                    </p>
                    <p className="text-[14px] text-primary">
                      {userRecord?.contactInfos[1]?.position}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AccountInformation;
